import { useTranslations } from 'next-intl'

import { PopularOption } from '@/entities/map'
import { AdditionalOption, BasicOption } from '@/entities/room'
import type { RoomIconName } from '@/entities/room'

export type Option<T> = {
  title: string
  value: T
  icon: RoomIconName
}

export const useOptions = () => {
  const t = useTranslations('common')

  const POPULAR_OPTIONS: Option<PopularOption>[] = [
    {
      title: t('popular_option.parking'),
      value: PopularOption.PARKING,
      icon: 'parking',
    },
    {
      title: t('popular_option.elevator'),
      value: PopularOption.ELEVATOR,
      icon: 'elevator',
    },
    {
      title: t('popular_option.pet'),
      value: PopularOption.PET_ALLOWED,
      icon: 'petAllowed',
    },
    {
      title: t('popular_option.loft'),
      value: PopularOption.DUPLEX_STRUCTURE,
      icon: 'duplexStructure',
    },
  ]

  const BASIC_OPTIONS: Option<BasicOption>[] = [
    {
      title: t('basic_option.refrigerator'),
      value: BasicOption.REFRIGERATOR,
      icon: 'refrigerator',
    },
    {
      title: t('basic_option.washer'),
      value: BasicOption.WASHING_MACHINE,
      icon: 'washingMachine',
    },
    {
      title: t('basic_option.air_conditioner'),
      value: BasicOption.AIR_CONDITIONER,
      icon: 'airConditioner',
    },
    {
      title: t('basic_option.tv'),
      value: BasicOption.TV,
      icon: 'tv',
    },
    {
      title: t('basic_option.wifi'),
      value: BasicOption.WIFI,
      icon: 'wifi',
    },
    {
      title: t('basic_option.sink'),
      value: BasicOption.SINK,
      icon: 'sink',
    },
    {
      title: t('basic_option.gas_range'),
      value: BasicOption.GAS_STOVE,
      icon: 'gasStove',
    },
    {
      title: t('basic_option.induction'),
      value: BasicOption.INDUCTION,
      icon: 'induction',
    },
    {
      title: t('basic_option.bed'),
      value: BasicOption.BED,
      icon: 'bed',
    },
  ]

  const ADDITIONAL_OPTIONS: Option<AdditionalOption>[] = [
    {
      title: t('additional_option.door_lock'),
      value: AdditionalOption.DOOR_LOCK,
      icon: 'doorlock',
    },
    {
      title: t('additional_option.cctv'),
      value: AdditionalOption.CCTV,
      icon: 'cctv',
    },
    {
      title: t('additional_option.management_office'),
      value: AdditionalOption.MANAGEMENT_OFFICE,
      icon: 'managementOffice',
    },
    {
      title: t('additional_option.dining_table'),
      value: AdditionalOption.DINING_TABLE,
      icon: 'diningTable',
    },
    {
      title: t('additional_option.electric_stove'),
      value: AdditionalOption.MICROWAVE,
      icon: 'microwave',
    },
    {
      title: t('additional_option.electric_rice_cooker'),
      value: AdditionalOption.RICE_COOKER,
      icon: 'riceCooker',
    },
    {
      title: t('additional_option.water_purifier'),
      value: AdditionalOption.WATER_PURIFIER,
      icon: 'waterPurifier',
    },
    {
      title: t('additional_option.sofa'),
      value: AdditionalOption.SOFA,
      icon: 'sofa',
    },
    {
      title: t('additional_option.desk'),
      value: AdditionalOption.DESK,
      icon: 'desk',
    },
    {
      title: t('additional_option.closet'),
      value: AdditionalOption.CLOSET,
      icon: 'closet',
    },
    {
      title: t('additional_option.shoe_rack'),
      value: AdditionalOption.SHOE_RACK,
      icon: 'shoeRack',
    },
    {
      title: t('additional_option.dressing_table'),
      value: AdditionalOption.VANITY,
      icon: 'vanity',
    },
    {
      title: t('additional_option.bathtub'),
      value: AdditionalOption.BATHTUB,
      icon: 'bathtub',
    },
    {
      title: t('additional_option.bidet'),
      value: AdditionalOption.BIDET,
      icon: 'bidet',
    },
    {
      title: t('additional_option.dryer'),
      value: AdditionalOption.DRYER,
      icon: 'dryer',
    },
    {
      title: t('additional_option.balcony'),
      value: AdditionalOption.BALCONY,
      icon: 'balcony',
    },
    {
      title: t('additional_option.dress_room'),
      value: AdditionalOption.DRESSING_ROOM,
      icon: 'dressingRoom',
    },
    {
      title: t('additional_option.cable_tv'),
      value: AdditionalOption.CABLE_TV,
      icon: 'cableTV',
    },
    {
      title: t('additional_option.curtain'),
      value: AdditionalOption.CURTAINS,
      icon: 'curtain',
    },
    {
      title: t('additional_option.air_purifier'),
      value: AdditionalOption.AIR_PURIFIER,
      icon: 'airPurifier',
    },
  ]

  return { POPULAR_OPTIONS, BASIC_OPTIONS, ADDITIONAL_OPTIONS }
}
